/**
 * Parameters for {@link startDownload}.
 */
export interface StartDownloadParams {
  /** The name of the file to be saved on the user's device. */
  filename: string;
  /** The file content to download, provided as a {@link Blob} or {@link File}. */
  content: Blob;
}

/**
 * Triggers a browser download for the given content by creating a temporary
 * anchor element, pointing it at an object URL, and programmatically clicking it.
 *
 * The temporary anchor and object URL are cleaned up immediately after the click.
 *
 * @param params - The download parameters.
 * @param params.filename - The suggested file name for the downloaded file.
 * @param params.content - The file content to download.
 *
 * @example
 * ```ts
 * startDownload({
 *   filename: 'report.json',
 *   content: new Blob([JSON.stringify(data)], { type: 'application/json' }),
 * });
 * ```
 */
export function startDownload({ filename, content }: StartDownloadParams): void {
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = URL.createObjectURL(content);
  a.download = filename;
  document.body.append(a);
  a.click();
  URL.revokeObjectURL(a.href);
  a.remove();
}
